import { createWelcomeStep, createMiddleStep, createFinalStep } from '../config/stepTemplates';
import { waitForElement, waitForReduxState, fillReactInput } from '../utils/tourUtils';
import { updateCountryPreference } from '../../../redux/actions/userAction';
import { resetSearchStore } from '../../../redux/actions/searchAction';
import {
  setSearchFilters,
  paginateSearch,
  setSearchSelectedCitation,
  setSearchDocument,
  setSearchLoadingDocument,
  setSearchDocumentSourceUrl,
} from '../../../redux/actions/searchAction';
import { backendClient } from '../../../api/backend';

const TOTAL = 9;

// The specific citation to open in the tour
const TOUR_CITATION = {
  id: 'a2b107f2-a633-4e82-b0dd-fab5433c43de',
  metadata: {
    chunk_id: 'a2b107f2-a633-4e82-b0dd-fab5433c43de',
    document_id: '66adc91e-872a-55ce-a31f-88fd1d822c83',
    section_title: 'article 1227-2',
    doc_type: 'Code',
    language: 'fr',
    title: 'Code des obligations et des contrats',
    doc_number: '12-08-1913',
    files: {
      html: {
        fr: 'MA/law/sgg.gov.ma_fr/fr/faa99473-0992-579f-ba37-83e98d22be97.html',
      },
    },
    country: 'MA',
  },
  text: "Article 1227-2 : (institué par l'article 2 de la loi n° 21-18 promulguée par le dahir n° 1-19-76 du 11 chaabane 1440 (17 avril 2019)) Le gage ou le nantissement peut, dans la mesure du possible, faire l'objet de réalisation partielle.",
};

export function getSearchTourSteps(t, { dispatch, store, regionContent }) {
  return [
    // 1 — Welcome (centered) — reset search state to show landing
    {
      ...createWelcomeStep({
        id: 'search-welcome',
        title: t('onboarding_tour.search.step1.title', 'Legal Search Engine'),
        text: t('onboarding_tour.search.step1.text', 'Search across thousands of laws, regulations, and court decisions in seconds.'),
        t,
        totalSteps: TOTAL,
      }),
      when: {
        show() {
          dispatch(resetSearchStore());
        },
      },
    },

    // 2 — Mode selector
    {
      ...createMiddleStep({
        id: 'search-mode',
        title: t('onboarding_tour.search.step2.title', 'Choose Your Source'),
        text: t('onboarding_tour.search.step2.text', 'Select what to search: Laws & Regulations (📜), Specific Regulations (📋), or Case Law (⚖️). You can select multiple.'),
        attachTo: { element: '[data-tour="search-mode-selector"]', on: 'bottom' },
        t,
        stepIndex: 2,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="search-mode-selector"]', 3000).catch(() => {});
      },
    },

    // 3 — Country selector
    {
      ...createMiddleStep({
        id: 'search-country',
        title: t('onboarding_tour.search.step3.title', 'Select Jurisdiction'),
        text: t(`onboarding_tour.search.step3.text_${regionContent.countryCode}`,
          `Choose which country's legal system to search. Currently searching in ${regionContent.jurisdictionLabel}.`),
        attachTo: { element: '[data-tour="search-country-selector"]', on: 'bottom' },
        t,
        stepIndex: 3,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="search-country-selector"]', 3000).catch(() => {});
      },
    },

    // 4 — Auto-fill + search → wait for results → auto-advance
    {
      id: 'search-input',
      title: t('onboarding_tour.search.step4.title', 'Try a Search'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((4 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 4, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.search.step4.text', "Let's search for something! Type a query or we'll fill one for you.")}</p>`,
      attachTo: { element: '.MuiAutocomplete-root', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.search_btn', 'Search →'),
          classes: 'shepherd-button-primary lawis-search-btn',
          action: function () {
            const btn = document.querySelector('.lawis-search-btn');
            if (btn) {
              btn.textContent = '⏳ ' + t('onboarding_tour.search.step5.title', 'Searching...');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0.7';
            }

            const sendBtn = document.querySelector('[data-tour="search-send-btn"]');
            if (sendBtn) sendBtn.click();

            const tour = this;

            // Wait for results, then advance to results step
            waitForReduxState(
              store,
              (s) => !s.search.isLoadingSearchCitations && s.search.filteredCitations?.length > 0,
              20000
            )
              .then(() => waitForElement('[data-tour="search-citation-card"]', 5000))
              .then(() => setTimeout(() => tour.next(), 300))
              .catch(() => tour.next());
          },
        },
      ],
      when: {
        show() {
          dispatch(updateCountryPreference([regionContent.countryCode]));
          const input = document.querySelector('.MuiAutocomplete-root input');
          if (input && !input.value) {
            fillReactInput('.MuiAutocomplete-root input', regionContent.searchDemoQuery);
          }
        },
      },
    },

    // 5 — Show results (no filter yet — just explain what they see)
    {
      ...createMiddleStep({
        id: 'search-results',
        title: t('onboarding_tour.search.step6.title', 'Search Results'),
        text: t('onboarding_tour.search.step6.text', 'Your results are here! Each card shows the document title, a relevant extract, date, and jurisdiction. Results are ranked by relevance.'),
        attachTo: { element: '[data-tour="search-results-area"]', on: 'right' },
        t,
        stepIndex: 5,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="search-results-area"]', 5000).catch(() => {});
      },
    },

    // 6 — Show filter sidebar + apply "Code" filter
    {
      id: 'search-filters',
      title: t('onboarding_tour.search.step10.title', 'Refine with Filters'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((6 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 6, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.search.step10.text', 'Use advanced filters to narrow results by date, document type, jurisdiction, court, and more.')}</p>`,
      attachTo: { element: '[data-tour="search-filter-doc-types"]', on: 'right' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.next', 'Next'),
          classes: 'shepherd-button-primary lawis-filter-btn',
          action: function () {
            // Show loading on button
            const btn = document.querySelector('.lawis-filter-btn');
            if (btn) {
              btn.textContent = '⏳ ' + t('onboarding_tour.search.filtering', 'Filtering...');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0.7';
            }

            const tour = this;
            const state = store.getState();
            const searchId = state.search.currentSearchId;
            const filters = { doc_types: ['Code'] };
            dispatch(setSearchFilters(filters));
            dispatch(paginateSearch(searchId, filters, 1))
              .then(() => new Promise((r) => setTimeout(r, 500)))
              .then(() => waitForElement('[data-tour="search-citation-card"]', 5000))
              .then(() => tour.next())
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="search-filter-doc-types"]', 3000).catch(() => {});
      },
    },

    // 7 — Select citation (after Code filter applied) — re-attach to first card
    {
      id: 'search-select-citation',
      title: t('onboarding_tour.search.step7.title', 'Open a Document'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((7 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 7, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.search.step7.text', 'Click on this result to view the full document. The relevant section will be highlighted.')}</p>`,
      attachTo: { element: '[data-tour="search-target-citation"]', on: 'right' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.next', 'Next'),
          classes: 'shepherd-button-primary lawis-open-doc-btn',
          action: function () {
            // Show loading
            const btn = document.querySelector('.lawis-open-doc-btn');
            if (btn) {
              btn.textContent = '⏳ ' + t('onboarding_tour.loading', 'Loading...');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0.7';
            }

            const tour = this;
            dispatch(setSearchSelectedCitation({
              id: TOUR_CITATION.id,
              metadata: TOUR_CITATION.metadata,
              page_content: TOUR_CITATION.text,
            }));
            dispatch(setSearchLoadingDocument(true));

            const blobName = TOUR_CITATION.metadata.files?.html?.fr;
            const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME;
            if (blobName && containerName) {
              backendClient.getDocumentSas(containerName, blobName)
                .then((data) => {
                  if (data?.blob_url_with_sas) {
                    dispatch(setSearchDocument({
                      id: TOUR_CITATION.metadata.document_id,
                      url: data.blob_url_with_sas,
                      language: 'fr',
                    }));
                    dispatch(setSearchDocumentSourceUrl(data.blob_url_with_sas));
                  }
                  dispatch(setSearchLoadingDocument(false));
                })
                .catch(() => dispatch(setSearchLoadingDocument(false)));
            }

            waitForReduxState(
              store,
              (s) => !s.search.isSearchLoadingDocument && s.search.searchDocument,
              10000
            )
              .then(() => setTimeout(() => tour.next(), 500))
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="search-citation-card"]', 5000)
          .then(() => {
            // Find the second citation card and tag it for targeting
            const cards = document.querySelectorAll('[data-tour="search-citation-card"]');
            const target = cards.length >= 2 ? cards[1] : cards[0];
            if (target) {
              target.setAttribute('data-tour', 'search-target-citation');
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return new Promise((r) => setTimeout(r, 400));
          })
          .catch(() => {});
      },
    },

    // 8 — Document viewer
    {
      ...createMiddleStep({
        id: 'search-docviewer',
        title: t('onboarding_tour.search.step8.title', 'Document Viewer'),
        text: t('onboarding_tour.search.step8.text', 'The full document is displayed here with the matching section highlighted. Scroll through the entire law or court decision.'),
        attachTo: { element: '[data-tour="search-doc-viewer"]', on: 'left' },
        t,
        stepIndex: 8,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="search-doc-viewer"]', 5000).catch(() => {});
      },
    },

    // 9 — Translation
    {
      ...createFinalStep({
        id: 'search-translation',
        title: t('onboarding_tour.search.step9.title', 'Translate Documents'),
        text: t('onboarding_tour.search.step9.text', 'Translate any document into 15+ languages. Select a language from this dropdown to see the translation in real-time.'),
        attachTo: { element: '.htmlviewer-translate-select', on: 'bottom' },
        t,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('.htmlviewer-translate-select', 5000).catch(() => {});
      },
    },
  ];
}
